/**
 * React Hook for Real-Time Price Updates via Backend WebSocket
 *
 * Routes through the backend /api/v1/market/ws/prices endpoint, which
 * subscribes to the single shared EODHD upstream connection via Redis.
 *
 * Usage:
 * const price = useRealtimePrice('XAUUSD.FOREX');
 * const btcPrice = useRealtimePrice('BTC-USD.CC');
 */

import { useMemo } from 'react';
import { useMarketWS } from './useMarketWS';

interface RealtimePrice {
  symbol: string;
  price?: number;
  bid?: number;
  ask?: number;
  change?: number;
  changePercent?: number;
  timestamp?: number;
  isLive: boolean;
}

export function useRealtimePrice(symbol: string): RealtimePrice {
  const symbols = useMemo(() => (symbol ? [symbol] : []), [symbol]);
  const { ticks } = useMarketWS({ symbols });

  const tick = ticks[symbol];
  if (!tick) {
    return { symbol, isLive: false };
  }

  return {
    symbol,
    price: tick.price,
    bid: tick.bid,
    ask: tick.ask,
    change: tick.change,
    changePercent: tick.change_pct,
    timestamp: new Date(tick.timestamp).getTime(),
    isLive: true,
  };
}

/**
 * Hook to subscribe to multiple symbols at once
 */
export function useRealtimePrices(symbols: string[]): Map<string, RealtimePrice> {
  const { ticks } = useMarketWS({ symbols });

  return useMemo(() => {
    const map = new Map<string, RealtimePrice>();
    symbols.forEach(symbol => {
      const tick = ticks[symbol];
      map.set(symbol, tick
        ? {
            symbol,
            price: tick.price,
            bid: tick.bid,
            ask: tick.ask,
            change: tick.change,
            changePercent: tick.change_pct,
            timestamp: new Date(tick.timestamp).getTime(),
            isLive: true,
          }
        : { symbol, isLive: false }
      );
    });
    return map;
  }, [ticks, symbols]);
}

export default useRealtimePrice;
